import { defineStore } from 'pinia';
import type { RemovableRef } from '@vueuse/core';
import { useLocalStorage } from '@vueuse/core';
import {
  formatAddress,
  getAddressSystem,
  isNameValid,
} from '@sonarwatch/portfolio-core';
import { nanoid } from 'nanoid';
import { BUNDLE_MAX_SIZE, addressesSeparator } from '@/constants';
import { getOwner } from '@/api/apiMain';
import { useSnackbarStore } from './snackbar';
import { shortAddress } from '@/helpers/shortAddress';

export type AddressBookItem = {
  address: string;
  name: string;
  pinned?: boolean;
};
export type AddressBookBundle = {
  id: string;
  name: string;
  items: AddressBookItem[];
  addresses: string;
  pinned?: boolean;
};

export type AddressBookBackup = {
  items: AddressBookItem[];
  bundles: AddressBookBundle[];
  createdAt: number;
};

export const maxRecent = 5;
export const maxPin = 5;

interface State {
  bundleDialog: boolean;
  selected: RemovableRef<string | null>;
  nBundleTotal: RemovableRef<number>;
  items: RemovableRef<AddressBookItem[]>;
  recentItems: RemovableRef<AddressBookItem[]>;
  bundles: RemovableRef<AddressBookBundle[]>;
  editBundle: AddressBookBundle;
}

export const useAddressBookStore = defineStore('addressBook', {
  state: (): State => ({
    bundleDialog: false,
    selected: useLocalStorage('pinia/addressBook/selected', null),
    nBundleTotal: useLocalStorage('pinia/addressBook/nTotal', 0),
    items: useLocalStorage('pinia/addressBook/items', []),
    recentItems: useLocalStorage('pinia/addressBook/recentItems', []),
    bundles: useLocalStorage('pinia/addressBook/bundles/v2', []),
    editBundle: {
      id: nanoid(),
      name: 'Bundle #0',
      items: [],
      addresses: '',
    },
  }),
  getters: {
    isEmpty: (state) => {
      return state.items.length === 0 && state.bundles.length === 0;
    },
    getItem: (state) => {
      return (address: string) =>
        state.items.find((i) => i.address === address);
    },
    getEditBundleItem(state) {
      return (address: string) =>
        state.editBundle.items.find((i) => i.address === address);
    },
    isSaved: (state) => {
      return (address: string) => {
        const addressSystem = getAddressSystem(address);
        if (!addressSystem) return false;
        const fAddress = formatAddress(address, addressSystem);
        return state.items.some((i) => i.address === fAddress);
      };
    },
    getBundle: (state) => {
      return (bundleId: string) => {
        const bundle = state.bundles.find((b) => b.id === bundleId);
        if (!bundle) throw new Error(`Bundle id is not valide: ${bundleId}`);
        return bundle;
      };
    },
    getFirstAsSelected: (state) => {
      if (state.items.length > 0) return state.items[0].address;
      if (state.bundles.length > 0) return state.bundles[0].id;
      return null;
    },
    getTo: (state) => {
      if (!state.selected)
        return {
          name: 'AddressBook',
        };

      const bundle = state.bundles.find((b) => b.id === state.selected);
      if (bundle) {
        return {
          name: 'Portfolio',
          params: {
            addresses: bundle.addresses,
            bundleId: bundle.id,
          },
        };
      }

      return {
        name: 'Portfolio',
        params: {
          addresses: state.selected,
        },
      };
    },
  },
  actions: {
    openBundleDialog(bundleId?: string) {
      if (bundleId) {
        this.editBundle = JSON.parse(JSON.stringify(this.getBundle(bundleId)));
      } else {
        this.editBundle = {
          id: nanoid(),
          name: `Bundle #${this.nBundleTotal + 1}`,
          items: [],
          addresses: '',
        };
      }
      this.bundleDialog = true;
    },
    closeBundleDialog() {
      this.bundleDialog = false;
    },
    async addAddress(input: string, providedName?: string) {
      let name: string | undefined = providedName;
      let address: string | undefined;
      const snackbarStore = useSnackbarStore();

      const trimmedInput = input.trim();
      const addressSystem = getAddressSystem(trimmedInput);

      if (addressSystem) {
        address = formatAddress(trimmedInput, addressSystem);
      } else if (isNameValid(trimmedInput)) {
        const owner = await getOwner(trimmedInput);
        if (!owner) return;
        address = owner.address;
        name = trimmedInput;
      } else {
        snackbarStore.push({
          text: 'Address is not valid.',
          closable: true,
        });
        return;
      }
      if (this.items.some((i) => i.address === address)) return;
      const item: AddressBookItem = {
        address,
        name: name ? name : shortAddress(address),
      };
      // @disabled-jup
      if (getAddressSystem(address) !== 'solana') {
        snackbarStore.push({
          text: 'Unvalid Solana address',
          closable: true,
        });
        return;
      }
      this.items.push(item);
      if (!this.selected) this.selected = address;
    },
    async addAddressToEditBundle(input: string) {
      if (this.editBundle.items.length >= BUNDLE_MAX_SIZE) return;
      let name: string | undefined;
      let address: string | undefined;
      const trimmedInput = input.trim();

      const snackbarStore = useSnackbarStore();

      const addressSystem = getAddressSystem(trimmedInput);
      if (addressSystem) {
        address = formatAddress(trimmedInput, addressSystem);
      } else if (isNameValid(trimmedInput)) {
        const owner = await getOwner(trimmedInput);
        if (!owner) return;
        address = owner.address;
        name = trimmedInput;
      } else {
        snackbarStore.push({
          text: 'Address is not valid.',
          closable: true,
        });
        return;
      }
      if (this.editBundle.items.some((i) => i.address === address)) return;

      if (name === undefined) name = this.getItem(address)?.name;
      if (name === undefined) name = shortAddress(address);
      // @disabled-jup
      if (getAddressSystem(address) !== 'solana') {
        const snackbarStore = useSnackbarStore();
        snackbarStore.push({
          text: 'Unvalid Solana address',
          closable: true,
        });
        return;
      }
      const item: AddressBookItem = {
        address,
        name: name,
      };
      this.editBundle.items.push(item);
      this.editBundle.addresses = this.editBundle.items
        .map((i) => i.address)
        .join(addressesSeparator);
    },
    removeAddressFromEditBundle(address: string) {
      this.editBundle.items = this.editBundle.items.filter(
        (i) => i.address !== address,
      );
      this.editBundle.addresses = this.editBundle.items
        .map((i) => i.address)
        .join(addressesSeparator);
    },
    saveEditBundle() {
      const index = this.bundles.findIndex((b) => b.id === this.editBundle.id);
      if (this.editBundle.name === '')
        this.editBundle.name = this.editBundle.id;
      if (index !== -1) {
        this.bundles[index] = JSON.parse(JSON.stringify(this.editBundle));
      } else {
        this.bundles.push(JSON.parse(JSON.stringify(this.editBundle)));
        if (!this.selected) this.selected = this.editBundle.id;
        this.nBundleTotal += 1;
      }
    },
    removeBundle(bundleId: string) {
      this.bundles = this.bundles.filter((b) => b.id !== bundleId);
      if (bundleId === this.selected) this.selected = this.getFirstAsSelected;
    },
    removeAddress(address: string) {
      const addressSystem = getAddressSystem(address);
      if (!addressSystem) return false;
      const fAddress = formatAddress(address, addressSystem);
      this.items = this.items.filter((i) => i.address !== fAddress);
      if (fAddress === this.selected) this.selected = this.getFirstAsSelected;
    },
    pinAddress(address: string, pinned = true) {
      if (
        pinned &&
        this.items.filter((i) => i.pinned).length +
          this.bundles.filter((b) => b.pinned).length >=
          maxPin
      ) {
        const snackbarStore = useSnackbarStore();
        snackbarStore.push({
          text: `Pinned addresses limit reached (${maxPin})`,
          closable: true,
        });
        return;
      }
      const addressSystem = getAddressSystem(address);
      if (!addressSystem) return false;
      const fAddress = formatAddress(address, addressSystem);
      this.items = this.items.map((i) => {
        if (i.address === fAddress) i.pinned = pinned;
        return i;
      });
    },
    pinBundle(bundleId: string, pinned = true) {
      if (
        pinned &&
        this.items.filter((i) => i.pinned).length +
          this.bundles.filter((b) => b.pinned).length >=
          maxPin
      ) {
        const snackbarStore = useSnackbarStore();
        snackbarStore.push({
          text: `Pinned addresses limit reached (${maxPin})`,
          closable: true,
        });
        return;
      }
      const index = this.bundles.findIndex((b) => b.id === bundleId);
      if (index >= 0) this.bundles[index].pinned = pinned;
    },
    select(addressesString: string) {
      const bundle = this.bundles.find((b) => b.addresses === addressesString);
      if (bundle) {
        this.selected = bundle.id;
      } else {
        const address = this.items.find((i) => i.address === addressesString);
        if (address) {
          this.selected = address.address;
        }
      }
    },
    async addRecentAddress(addresses: string[]) {
      if (addresses.length !== 1) return;

      const trimmedInput = addresses[0].trim();
      const addressSystem = getAddressSystem(trimmedInput);

      let name: string | undefined;
      let address: string | undefined;

      const existingItem = this.items.find((i) => i.address === address);
      if (existingItem) {
        name = existingItem.name;
      } else if (addressSystem) {
        address = formatAddress(trimmedInput, addressSystem);
      } else if (isNameValid(trimmedInput)) {
        const owner = await getOwner(trimmedInput);
        if (!owner) return;
        address = owner.address;
        name = trimmedInput;
      }

      if (address) {
        if (this.recentItems.some((i) => i.address === address)) {
          this.recentItems.splice(
            this.recentItems.findIndex((i) => i.address === address),
            1,
          );
        } else if (this.recentItems.length >= maxRecent) {
          this.recentItems.splice(0, 1);
        }

        this.recentItems.push({
          address,
          name: name || shortAddress(address),
        });
      }
    },
    moveItem(from: number, to: number) {
      const item = this.items.splice(from, 1)[0];
      this.items.splice(to, 0, item);
    },
    moveAddressFromEditBundle(from: number, to: number) {
      const item = this.editBundle.items.splice(from, 1)[0];
      this.editBundle.items.splice(to, 0, item);
      this.editBundle.addresses = this.editBundle.items
        .map((i) => i.address)
        .join(addressesSeparator);
    },
    importBundles(backup: AddressBookBackup) {
      this.items = backup.items;
      this.bundles = backup.bundles;
    },
  },
});
